import React, { useEffect, useState, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import useSound from "use-sound";
import Header from "./Header";
import useSize from "../../../../Utils/useSize/useSize";
import NotFound from "../../../../Utils/NotFound/NotFound";
import ErrorSound from "../../../../Sound/Error.wav";
import BuySound from "../../../../Sound/buy.mp3";
import SellSound from "../../../../Sound/sell.mp3";
import Bid from "./Bid";
import Ask from "./Ask";
import Backend from "../../../../Backend/Backend";

export default function BuySellPending() {
  const windowSize = useSize();
  const dropdownRef = useRef(null);
  const [backend, setBackend] = useState(null);

  const [errorsound] = useSound(ErrorSound, { volume: 0.5 });
  const [buysound] = useSound(BuySound, { volume: 0.5 });
  const [sellsound] = useSound(SellSound, { volume: 0.5 });

  const [symbol, setSymbol] = useState(null);
  const [qty, setQty] = useState("");
  const [orderType, setOrderType] = useState("Market Execution");
  const [openType, setOpenType] = useState(false);
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [loading, setLoading] = useState(false);

  // 🧠 Local alert states
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("info"); // success | error

  // ⚙️ Inline alert
  const showAlert = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(null), 3000);
  };

  // ✅ Initialize backend
  useEffect(() => {
    (async () => {
      const b = await new Backend().init();
      setBackend(b);
    })();
  }, []);

  // ✅ Load symbol from memory
  useEffect(() => {
    const savedSymbol =
      window.symboldatapending ||
      JSON.parse(localStorage.getItem("symboldatapending") || "null");
    if (savedSymbol) {
      setSymbol(savedSymbol);
      localStorage.setItem("symboldatapending", JSON.stringify(savedSymbol));
    }
  }, []);

  // ✅ Update order type when symbol changes
  useEffect(() => {
    if (symbol && typeof symbol.limit_sl !== "undefined") {
      setOrderType(symbol.limit_sl === 0 ? "Take Profit" : "Stop Loss");

      if (symbol && typeof symbol.price !== "undefined") {
        if (symbol.limit_sl === 0) {
          setTakeProfit(symbol.price);
        } else {
          setStopLoss(symbol.price);
        }
      }
    }
    if (symbol && typeof symbol.qty !== "undefined") {
      setQty(
        symbol.type === "nse" ||
          symbol.type === "nse_cash" ||
          symbol.type === "us_stock" ||
          symbol.type === "sgx" ||
          symbol.type === "index"
          ? symbol.qty
          : symbol.qty / symbol.lot_size,
      );
    }
  }, [symbol]);

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenType(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!symbol)
    return (
      <div className="flex justify-center items-center text-gray-400">
        <NotFound
          small
          height={windowSize[1] - 100}
          title="Nothing here"
          subtext="Loading symbol"
        />
      </div>
    );

  // ✅ Submit order
  const handleSubmit = async (side) => {
    const soundOn = JSON.parse(localStorage.getItem("sound")); // <-- check sound setting

    const playBuySound = () => soundOn && buysound();
    const playSellSound = () => soundOn && sellsound();
    const playErrorSound = () => soundOn && errorsound();

    if (!backend)
      return (showAlert("Backend not initialized", "error"), playErrorSound());

    if (side !== 0 && side !== 1)
      return (showAlert("Buy/Sell not initialized", "error"), playErrorSound());

    if (!symbol)
      return (showAlert("Select a symbol first", "error"), playErrorSound());

    // ------------------ VALIDATION ------------------
    if (orderType === "Market Execution") {
      if (!qty || qty <= 0)
        return (showAlert("Enter a valid Quantity", "error"), playErrorSound());
    }

    if (orderType === "Take Profit") {
      if (!takeProfit || isNaN(takeProfit) || Number(takeProfit) <= 0)
        return (
          showAlert("Enter a valid Take Profit value", "error"),
          playErrorSound()
        );
    }

    if (orderType === "Stop Loss") {
      if (!stopLoss || isNaN(stopLoss) || Number(stopLoss) <= 0)
        return (
          showAlert("Enter a valid Stop Loss value", "error"),
          playErrorSound()
        );
    }

    // ------------------ ORDER PROCESS ------------------
    setLoading(true);

    try {
      const orderData = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        user_id: localStorage.getItem("id"),

        qty_order: qty,
        symbol_id: symbol.symbol_id,
        symbol: symbol.symbol_display ?? symbol.symbol,
        symbol_type: symbol.type,
        type: side,

        buy_sell:
          orderType === "Market Execution"
            ? 0
            : orderType === "Take Profit"
              ? 1
              : orderType === "Stop Loss"
                ? 2
                : 0,

        price:
          orderType === "Market Execution"
            ? null
            : orderType === "Take Profit"
              ? takeProfit
              : orderType === "Stop Loss"
                ? stopLoss
                : 0,

        pending_edit: "1",
        pending_id: symbol.id,
      };

      const r = await backend.buy_sell(orderData);
      setLoading(false);

      if (r.error === "False") {
        showAlert(r.message, "success");

        // 🔊 SOUND WHEN SUCCESS
        side === 1 ? playBuySound() : playSellSound();

        setQty("");
        setStopLoss("");
        setTakeProfit("");
      } else {
        showAlert(r.message, "error");
        playErrorSound();
      }
    } catch (err) {
      console.error(err);
      showAlert("Order failed. Try again.", "error");
      playErrorSound();
      setLoading(false);
    }
  };

  return (
    <div
      style={{ height: windowSize[1] - 100 }}
      className="bg-[var(--background)] flex flex-col relative"
    >
      <Header symbol={symbol} updateSymbol={setSymbol} />

      {message && (
        <div
          className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
      z-50 w-[90%] max-w-md p-6 rounded-2xl shadow-2xl border-2 text-center animate-zoomIn
      ${
        messageType === "success"
          ? "bg-green-50 border-green-400 text-green-800"
          : "bg-red-50 border-red-400 text-red-800"
      }`}
        >
          {/* 🔔 Icon */}
          <div className="flex justify-center mb-3">
            {messageType === "success" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-10 h-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-10 h-10 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </div>

          {/* 🧾 Title */}
          <h2 className="text-lg font-bold mb-2">
            {messageType === "success" ? "Success!" : "Alert!"}
          </h2>

          {/* 💬 Message */}
          <p className="text-sm mb-5 leading-relaxed">{message}</p>

          {/* ❌ Close Button */}
          <button
            onClick={() => setMessage("")}
            className={`px-5 py-2 rounded-lg font-semibold shadow 
        ${
          messageType === "success"
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-red-600 text-white hover:bg-red-700"
        }`}
          >
            OK
          </button>
        </div>
      )}

      <div className="flex-1 px-4 py-4 space-y-5 bg-[var(--background)] mt-2 shadow-sm relative">
        {/* 🔽 Order Type Dropdown */}
        <div ref={dropdownRef}>
          <p className="text-[var(--text)] mb-1 text-sm font-medium">
            Order Type
          </p>
          <div className="flex justify-between items-center border-2 border-[var(--hover)] rounded-md px-3 py-2 cursor-pointer bg-[var(--background)]">
            <span className="text-[var(--text)] text-sm">{orderType}</span>
            <FiChevronDown
              className={`text-[var(--text)] transition-transform duration-200 ${
                openType ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {/* 🧮 Quantity */}
        <div>
          <label className="text-[var(--text)] block mb-1">
            {symbol.type === "nse" || symbol.type === "nse_cash"
              ? "Quantity"
              : "Lot"}
          </label>
          <input
            disabled
            type="number"
            step="0.01"
            placeholder="0.00"
            value={qty}
            className="w-full border rounded-md px-3 py-2 text-[var(--text)] focus:outline-none focus:ring-1 border-2 border-[var(--hover)] bg-[var(--background)]"
          />
        </div>

        {/* ⛔ Stop Loss / Take Profit */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <label className="text-[var(--text)] block mb-1">Stop Loss</label>
            <input
              type="number"
              placeholder="0.00"
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
              disabled={orderType !== "Stop Loss"}
              className={`w-full border-2 border-[var(--hover)] text-[var(--text)] bg-[var(--background)] rounded-md px-3 py-2 ${
                orderType === "Stop Loss"
                  ? "border-[var(--sellmeta)]"
                  : "text-[var(--text)] cursor-not-allowed"
              }`}
            />
          </div>

          <div>
            <label className="text-[var(--text)] block mb-1">Take Profit</label>
            <input
              type="number"
              placeholder="0.00"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              disabled={orderType !== "Take Profit"}
              className={`w-full border-2 border-[var(--hover)] text-[var(--text)] bg-[var(--background)] rounded-md px-3 py-2 ${
                orderType === "Take Profit"
                  ? "border-[var(--primary)]"
                  : "text-[var(--text)] cursor-not-allowed"
              }`}
            />
          </div>
        </div>

        {/* 💰 Price Display */}
        <div className="flex justify-center items-center mt-6 mb-2">
          {symbol.buy_sell === 1 ? <Bid symbol_id={symbol.symbol_id} /> : null}
          {symbol.buy_sell === 0 ? <Ask symbol_id={symbol.symbol_id} /> : null}
        </div>

        {/* 🟢 Buy / Sell Buttons */}
        <div className="grid grid-cols-1 rounded-md overflow-hidden">
          {symbol.buy_sell === 1 ? (
            <button
              disabled={loading}
              onClick={() => handleSubmit(1)}
              className="bg-[#d84e4e] text-white py-3 font-semibold text-sm active:scale-95 transition disabled:opacity-50"
            >
              Sell by Market
            </button>
          ) : null}
          {symbol.buy_sell === 0 ? (
            <button
              disabled={loading}
              onClick={() => handleSubmit(0)}
              className="bg-[#3973e6] text-white py-3 font-semibold text-sm active:scale-95 transition disabled:opacity-50"
            >
              Buy by Market
            </button>
          ) : null}
        </div>

        <p className="text-[11px] text-center text-gray-500 mt-4 leading-snug">
          Attention! The trade will be executed at market conditions. Difference
          with requested price may be significant!
        </p>
      </div>
    </div>
  );
}
